import React, { useState } from 'react';
import { Download } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authService, getErrorMessage } from '../services/api';
import { dataExportFilename, saveFile } from '../utils/download';
import { useToast } from './Toast';
import { Modal } from './ui';

// Account settings (docs/REQUIREMENTS.md ACC-9): the name, the newsletter consent (ACC-8), the password and a copy of
// the account's data (ACC-15). The email and the role are not the user's to change (admins do it).
const AccountSettings: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { user, setCurrentUser } = useAuth();
  const toast = useToast();
  const [fullName, setFullName] = useState(user?.full_name ?? '');
  const [newsletter, setNewsletter] = useState(!!user?.newsletter_opt_in);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  if (!user) return null;

  const profileChanged = fullName.trim() !== user.full_name || newsletter !== !!user.newsletter_opt_in;

  const saveProfile = async (event: React.FormEvent) => {
    event.preventDefault();
    setProfileError('');
    setSavingProfile(true);
    try {
      const subscribing = newsletter && !user.newsletter_opt_in;
      const updated = await authService.updateProfile({ full_name: fullName.trim(), newsletter_opt_in: newsletter });
      setCurrentUser(updated);
      toast.success(subscribing ? 'Saved. Check your inbox to confirm the newsletter.' : 'Account saved.');
    } catch (error) {
      setProfileError(getErrorMessage(error, 'Your account could not be saved.'));
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setPasswordError('');
    if (newPassword !== confirmPassword) {
      setPasswordError('The new passwords do not match.');
      return;
    }
    setSavingPassword(true);
    try {
      await authService.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password changed.');
    } catch (error) {
      setPasswordError(getErrorMessage(error, 'The password could not be changed.'));
    } finally {
      setSavingPassword(false);
    }
  };

  const downloadData = async () => {
    setExportError('');
    setExporting(true);
    try {
      saveFile(await authService.exportMyData(), dataExportFilename());
    } catch (error) {
      setExportError(getErrorMessage(error, 'Your data could not be downloaded. Please try again.'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal title="Account settings" description={user.email} onClose={onClose}>
      <form onSubmit={saveProfile} className="space-y-4">
        <div>
          <label htmlFor="account-name" className="label">Name</label>
          <input id="account-name" className="input" value={fullName} onChange={(event) => setFullName(event.target.value)} required maxLength={200} autoComplete="name" />
        </div>
        <label htmlFor="account-newsletter" className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
          <input id="account-newsletter" type="checkbox" className="checkbox mt-0.5" checked={newsletter} onChange={(event) => setNewsletter(event.target.checked)} />
          <span>
            Newsletter: new lessons and courses, about once a month.
            <span className="hint block">You can unsubscribe here at any time.</span>
          </span>
        </label>
        {profileError && <p className="alert alert-error">{profileError}</p>}
        <div className="flex justify-end">
          <button type="submit" className="btn btn-primary" disabled={!profileChanged || !fullName.trim() || savingProfile}>
            {savingProfile ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>

      <form onSubmit={savePassword} className="mt-6 space-y-4 border-t border-gray-200 dark:border-gray-800 pt-6">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Change password</h4>
        {/* For password managers: the account the password belongs to. */}
        <input type="email" value={user.email} autoComplete="username" readOnly hidden />
        <div>
          <label htmlFor="account-current-password" className="label">Current password</label>
          <input id="account-current-password" type="password" className="input" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} required autoComplete="current-password" />
        </div>
        <div>
          <label htmlFor="account-new-password" className="label">New password</label>
          <input id="account-new-password" type="password" className="input" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} required minLength={8} maxLength={128} autoComplete="new-password" />
          <p className="hint">At least 8 characters.</p>
        </div>
        <div>
          <label htmlFor="account-confirm-password" className="label">Repeat the new password</label>
          <input id="account-confirm-password" type="password" className="input" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} required minLength={8} maxLength={128} autoComplete="new-password" />
        </div>
        {passwordError && <p className="alert alert-error">{passwordError}</p>}
        <div className="flex justify-end">
          <button type="submit" className="btn btn-secondary" disabled={savingPassword}>
            {savingPassword ? 'Changing...' : 'Change Password'}
          </button>
        </div>
      </form>

      <section aria-labelledby="account-data-heading" className="mt-6 space-y-3 border-t border-gray-200 dark:border-gray-800 pt-6">
        <h4 id="account-data-heading" className="text-sm font-semibold text-gray-900 dark:text-white">Your data</h4>
        <p className="hint">
          A copy of everything we store about your account: profile, consents, plan, classes, submissions, drafts and
          simulation time, as a JSON file.
        </p>
        {exportError && <p className="alert alert-error">{exportError}</p>}
        <div className="flex justify-end">
          <button type="button" className="btn btn-secondary" onClick={downloadData} disabled={exporting}>
            <Download />
            <span>{exporting ? 'Preparing...' : 'Download my data'}</span>
          </button>
        </div>
      </section>
    </Modal>
  );
};

export default AccountSettings;
